import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { safeParseJSONAsync } from "@/lib/safeLocalStorage";

// Types from the HealthKit plugin
interface BaseData {
  startDate: string;
  endDate: string;
  source: string;
  uuid: string;
  sourceBundleId: string;
  duration: number;
}

interface SleepData extends BaseData {
  sleepState: string;
  timeZone: string;
}

interface OtherData extends BaseData {
  unitName: string;
  value: number;
}

interface QueryOutput<T> {
  countReturn: number;
  resultData: T[];
}

interface CapacitorHealthkitPlugin {
  requestAuthorization(authOptions: { read: string[]; write: string[]; all: string[] }): Promise<void>;
  queryHKitSampleType<T>(queryOptions: { sampleName: string; startDate: string; endDate: string; limit: number }): Promise<QueryOutput<T>>;
  isAvailable(): Promise<void>;
}

interface HealthKitData {
  bbt: { date: string; value: number }[];
  sleep: { date: string; hours: number; quality: string }[];
  steps: { date: string; count: number }[];
  activeEnergy: { date: string; calories: number }[];
  exerciseMinutes: { date: string; minutes: number }[];
}

interface HealthKitContextType {
  isAvailable: boolean;
  isAuthorized: boolean;
  isLoading: boolean;
  lastSyncDate: Date | null;
  data: HealthKitData;
  requestAuthorization: () => Promise<boolean>;
  syncData: () => Promise<void>;
  disconnect: () => void;
  syncBBT: () => Promise<{ date: string; value: number }[]>;
  syncSleep: () => Promise<{ date: string; hours: number; quality: string }[]>;
  syncActivity: () => Promise<{ steps: number; calories: number; exerciseMinutes: number }>;
}

const defaultData: HealthKitData = {
  bbt: [],
  sleep: [],
  steps: [],
  activeEnergy: [],
  exerciseMinutes: [],
};

const HealthKitContext = createContext<HealthKitContextType | undefined>(undefined);

// HealthKit plugin (will be loaded dynamically on iOS)
let CapacitorHealthkit: CapacitorHealthkitPlugin | null = null;

// Sample names enum values
const SampleNames = {
  STEP_COUNT: "stepCount",
  APPLE_EXERCISE_TIME: "appleExerciseTime",
  ACTIVE_ENERGY_BURNED: "activeEnergyBurned",
  SLEEP_ANALYSIS: "sleepAnalysis",
  BASAL_BODY_TEMPERATURE: "basalBodyTemperature",
  BODY_TEMPERATURE: "bodyTemperature",
} as const;

export const HealthKitProvider = ({ children }: { children: ReactNode }) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSyncDate, setLastSyncDate] = useState<Date | null>(() => {
    const saved = localStorage.getItem("healthkit_last_sync");
    return saved ? new Date(saved) : null;
  });
  const [data, setData] = useState<HealthKitData>(defaultData);

  // Load encrypted healthkit data asynchronously
  useEffect(() => {
    safeParseJSONAsync<HealthKitData>("healthkit_data", defaultData).then(setData);
  }, []);

  // Check if HealthKit is available (only on iOS)
  useEffect(() => {
    const checkAvailability = async () => {
      if (Capacitor.getPlatform() !== "ios") {
        setIsAvailable(false);
        return;
      }

      try {
        // Dynamically import the HealthKit plugin
        const healthKitModule = await import("@perfood/capacitor-healthkit");
        CapacitorHealthkit = healthKitModule.CapacitorHealthkit as CapacitorHealthkitPlugin;
        
        await CapacitorHealthkit.isAvailable();
        setIsAvailable(true);
        
        // Check if already authorized
        const savedAuth = localStorage.getItem("healthkit_authorized");
        if (savedAuth === "true") {
          setIsAuthorized(true);
        }
      } catch (error) {
        console.log("HealthKit not available:", error);
        setIsAvailable(false);
      }
    };

    checkAvailability();
  }, []);

  // Persist data to localStorage
  useEffect(() => {
    localStorage.setItem("healthkit_data", JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    if (lastSyncDate) {
      localStorage.setItem("healthkit_last_sync", lastSyncDate.toISOString());
    }
  }, [lastSyncDate]);

  const requestAuthorization = useCallback(async (): Promise<boolean> => {
    if (!isAvailable || !CapacitorHealthkit) {
      toast.error("HealthKit is not available on this device");
      return false;
    }

    setIsLoading(true);
    try {
      // Request read permissions for the data types we need
      await CapacitorHealthkit.requestAuthorization({
        all: [],
        read: [
          SampleNames.BASAL_BODY_TEMPERATURE,
          SampleNames.BODY_TEMPERATURE,
          SampleNames.SLEEP_ANALYSIS,
          SampleNames.STEP_COUNT,
          SampleNames.ACTIVE_ENERGY_BURNED,
          SampleNames.APPLE_EXERCISE_TIME,
        ],
        write: [],
      });

      setIsAuthorized(true);
      localStorage.setItem("healthkit_authorized", "true");
      toast.success("HealthKit connected successfully!");
      return true;
    } catch (error) {
      console.error("HealthKit authorization failed:", error);
      toast.error("Failed to connect to HealthKit");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAvailable]);

  const syncBBT = useCallback(async (): Promise<{ date: string; value: number }[]> => {
    if (!isAuthorized || !CapacitorHealthkit) return [];

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90); // Last 90 days

      // Try basal body temperature first, then regular body temperature
      let samples: OtherData[] = [];
      
      try {
        const basalResult = await CapacitorHealthkit.queryHKitSampleType<OtherData>({
          sampleName: SampleNames.BASAL_BODY_TEMPERATURE,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit: 0, // No limit
        });
        samples = basalResult.resultData || [];
      } catch {
        // Fallback to regular body temperature
        const tempResult = await CapacitorHealthkit.queryHKitSampleType<OtherData>({
          sampleName: SampleNames.BODY_TEMPERATURE,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit: 0,
        });
        samples = tempResult.resultData || [];
      }

      const bbtData = samples.map((sample) => {
        // HealthKit returns temperature in Celsius, convert to Fahrenheit for storage
        const tempC = sample.value;
        const tempF = tempC * (9 / 5) + 32;
        return {
          date: sample.startDate.split("T")[0],
          value: Number(tempF.toFixed(1)),
        };
      });

      // Deduplicate by date (keep latest reading per day)
      const uniqueBBT = bbtData.reduce((acc: { date: string; value: number }[], item) => {
        const existing = acc.findIndex((x) => x.date === item.date);
        if (existing >= 0) {
          acc[existing] = item;
        } else {
          acc.push(item);
        }
        return acc;
      }, []);

      return uniqueBBT;
    } catch (error) {
      console.error("Failed to sync BBT data:", error);
      return [];
    }
  }, [isAuthorized]);

  const syncSleep = useCallback(async (): Promise<{ date: string; hours: number; quality: string }[]> => {
    if (!isAuthorized || !CapacitorHealthkit) return [];

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30); // Last 30 days

      const result = await CapacitorHealthkit.queryHKitSampleType<SleepData>({
        sampleName: SampleNames.SLEEP_ANALYSIS,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: 0,
      });

      const samples: SleepData[] = result.resultData || [];
      
      // Group sleep sessions by date and calculate total sleep
      const sleepByDate: Record<string, { totalMinutes: number; inBedMinutes: number }> = {};

      samples.forEach((sample) => {
        const date = sample.startDate.split("T")[0];
        const durationMinutes = sample.duration / 60; // duration is in seconds

        if (!sleepByDate[date]) {
          sleepByDate[date] = { totalMinutes: 0, inBedMinutes: 0 };
        }

        // Check sleep state - states that count as sleep
        const asleepStates = ["ASLEEP", "ASLEEP_CORE", "ASLEEP_DEEP", "ASLEEP_REM", "asleepCore", "asleepDeep", "asleepREM"];
        if (asleepStates.some(state => sample.sleepState.includes(state) || sample.sleepState === state)) {
          sleepByDate[date].totalMinutes += durationMinutes;
        }
        sleepByDate[date].inBedMinutes += durationMinutes;
      });

      return Object.entries(sleepByDate).map(([date, data]) => {
        const hours = Number((data.totalMinutes / 60).toFixed(1));
        let quality = "fair";
        if (hours >= 7) quality = "good";
        if (hours >= 8) quality = "excellent";
        if (hours < 5) quality = "poor";
        
        return { date, hours, quality };
      });
    } catch (error) {
      console.error("Failed to sync sleep data:", error);
      return [];
    }
  }, [isAuthorized]);

  const syncActivity = useCallback(async (): Promise<{ steps: number; calories: number; exerciseMinutes: number }> => {
    if (!isAuthorized || !CapacitorHealthkit) return { steps: 0, calories: 0, exerciseMinutes: 0 };

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);

      // Get steps
      const stepsResult = await CapacitorHealthkit.queryHKitSampleType<OtherData>({
        sampleName: SampleNames.STEP_COUNT,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: 0,
      });
      const steps = (stepsResult.resultData || []).reduce(
        (total: number, sample: OtherData) => total + sample.value,
        0
      );

      // Get active energy
      const energyResult = await CapacitorHealthkit.queryHKitSampleType<OtherData>({
        sampleName: SampleNames.ACTIVE_ENERGY_BURNED,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: 0,
      });
      const calories = Math.round(
        (energyResult.resultData || []).reduce(
          (total: number, sample: OtherData) => total + sample.value,
          0
        )
      );

      // Get exercise minutes
      const exerciseResult = await CapacitorHealthkit.queryHKitSampleType<OtherData>({
        sampleName: SampleNames.APPLE_EXERCISE_TIME,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: 0,
      });
      const exerciseMinutes = Math.round(
        (exerciseResult.resultData || []).reduce(
          (total: number, sample: OtherData) => total + sample.value,
          0
        )
      );

      return { steps: Math.round(steps), calories, exerciseMinutes };
    } catch (error) {
      console.error("Failed to sync activity data:", error);
      return { steps: 0, calories: 0, exerciseMinutes: 0 };
    }
  }, [isAuthorized]);

  const syncData = useCallback(async () => {
    if (!isAuthorized) {
      toast.error("Please connect to HealthKit first");
      return;
    }

    setIsLoading(true);
    try {
      const [bbt, sleep, activity] = await Promise.all([
        syncBBT(),
        syncSleep(),
        syncActivity(),
      ]);

      const today = new Date().toISOString().split("T")[0];
      
      setData((prev) => ({
        ...prev,
        bbt,
        sleep,
        steps: [...prev.steps.filter((s) => s.date !== today), { date: today, count: activity.steps }],
        activeEnergy: [...prev.activeEnergy.filter((a) => a.date !== today), { date: today, calories: activity.calories }],
        exerciseMinutes: [...prev.exerciseMinutes.filter((e) => e.date !== today), { date: today, minutes: activity.exerciseMinutes }],
      }));

      setLastSyncDate(new Date());
      toast.success(`Synced ${bbt.length} BBT readings, ${sleep.length} sleep records`);
    } catch (error) {
      console.error("Failed to sync HealthKit data:", error);
      toast.error("Failed to sync HealthKit data");
    } finally {
      setIsLoading(false);
    }
  }, [isAuthorized, syncBBT, syncSleep, syncActivity]);

  const disconnect = useCallback(() => {
    setIsAuthorized(false);
    localStorage.removeItem("healthkit_authorized");
    localStorage.removeItem("healthkit_data");
    localStorage.removeItem("healthkit_last_sync");
    setData(defaultData);
    setLastSyncDate(null);
    toast.success("Disconnected from HealthKit");
  }, []);

  return (
    <HealthKitContext.Provider
      value={{
        isAvailable,
        isAuthorized,
        isLoading,
        lastSyncDate,
        data,
        requestAuthorization,
        syncData,
        disconnect,
        syncBBT,
        syncSleep,
        syncActivity,
      }}
    >
      {children}
    </HealthKitContext.Provider>
  );
};

export const useHealthKit = () => {
  const context = useContext(HealthKitContext);
  if (!context) {
    throw new Error("useHealthKit must be used within a HealthKitProvider");
  }
  return context;
};
